<template>
  <div id="programForm" class="form">
    <v-card-title>
      {{ id ? "Edit " : "Add " }} Checklist
      <v-spacer />

      <v-btn icon @click="goBack"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>

    <v-card-text class="">
      <v-form v-if="loaded" ref="form" v-model="valid" lazy-validation>
        <div class="d-flex justify-space-between mb-4">
          <v-switch
            inset
            dense
            label="Allow Global Check All"
            hide-details="auto"
            v-model="checklist.allowGlobalCheck"
          >
          </v-switch>
          <v-switch
            inset
            dense
            label="Allow Attachment"
            hide-details="auto"
            v-model="checklist.allowAttachment"
          >
          </v-switch>
        </div>
        <div class="d-flex justify-space-between mb-4">
          <v-switch
            inset
            dense
            label="Mandatory"
            hide-details="auto"
            v-model="checklist.mandatory"
          />
          <v-switch
            inset
            dense
            label="Show on Dashboard"
            hide-details="auto"
            v-model="checklist.showOnDashboard"
          />
        </div>

        <v-text-field
          class="mt-7"
          outlined
          clearable
          label="Name"
          v-model="checklist.checklistName"
          :rules="[rules.required]"
        />
        <v-textarea
          outlined
          auto-grow
          clearable
          :rows="1"
          label="Description"
          v-model="checklist.checklistDescription"
        />

        <v-switch
          inset
          dense
          label="Valid Date Range"
          hide-details="false"
          :rules="checklist.type === 'ONETIME' ? required : []"
          v-model="limited"
          class="mb-4"
        />
        <v-select
          outlined
          v-model="checklist.type"
          label="Checklist Type"
          :items="checklistTypes"
          item-text="text"
          item-value="value"
        />
        <v-select
          outlined
          v-model="checklist.selectionMethod"
          label="Selection Type"
          :items="selectionTypes"
          item-text="text"
          item-value="value"
        />

        <date-picker
          v-if="limited"
          v-model="validFrom"
          outlined
          label="Valid From"
          :rules="[validValidFrom]"
          clearable
          :defaultNowOnNull="false"
        />
        <date-picker
          v-if="limited"
          v-model="validTo"
          outlined
          label="Valid To"
          :rules="[validValidTo]"
          clearable
          :defaultNowOnNull="false"
        />

        <v-select
          outlined
          v-model="assignType"
          label="Assign Type (Leave empty to assign to everyone)"
          :items="assignTypes"
          item-text="text"
          item-value="value"
          clearable
        />
        <v-autocomplete
          v-if="assignType === 'PROGRAM'"
          outlined
          multiple
          chips
          label="Programs"
          hint="Leave Empty to Select All Programs"
          persistent-hint
          v-model="programIds"
          :items="programList"
          item-value="id"
          :item-text="getProgramText"
          hide-details="auto"
          clearable
        />
        <TeacherSelector
          v-if="assignType === 'TEACHER'"
          v-model="teacherIds"
          :initialTeachers="teacherIds"
          hint="Leave Empty to Select All Teacher"
          label="Teachers"
          multiple
        />
        <StudentSelector
          v-if="assignType === 'STUDENT'"
          v-model="studentIds"
          :initialStudents="studentIds"
          hint="Leave Empty to Select All Students"
          label="Students"
          multiple
        />
        <ClassroomSelector
          v-if="assignType === 'CLASSROOM'"
          v-model="classroomIds"
          hint="Leave Empty to Select All Classrooms"
          label="Classrooms"
          multiple
        />
        <div class="d-flex flex-column mt-7">
          <v-card
            v-for="(item, index) in checklist.items"
            :key="index"
            class="mb-5"
          >
            <v-card-text>
              <div class="d-flex flex-column">
                <div class="d-flex justify-space-between">
                  <span class="font-weight-bold">Checklist Item</span>
                  <v-btn icon @click="deleteItem(index)">
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </div>
                <v-text-field
                  class="mt-2"
                  dense
                  outlined
                  label="Name"
                  v-model="item.itemName"
                  hide-details="auto"
                />
                <v-textarea
                  class="mt-2"
                  dense
                  outlined
                  :rows="1"
                  auto-grow
                  label="Description"
                  v-model="item.itemDescription"
                  hide-details="auto"
                />
                <v-select
                  v-if="checklist.selectionMethod === 'MULTIPLE'"
                  class="mt-2"
                  dense
                  outlined
                  :items="['CHECKED', 'UNCHECKED', 'SKIPPED']"
                  label="Default Status on Check (CHECKED if not provided)"
                  v-model="item.defaultStatus"
                  hide-details="auto"
                />
                <v-checkbox
                  v-else
                  v-model="item.defaultStatus"
                  label="Make default (First item will be default if not provided.)"
                  true-value="CHECKED"
                  false-value="UNCHECKED"
                  @change="handleCheckbox(index)"
                ></v-checkbox>
                <div class="d-flex flex-column">
                  <!-- <v-switch
                    label="Notify me if checklist is"
                    inset
                    dense
                    hide-details="auto"
                  ></v-switch>
                  <div class="d-flex align-center mt-5">
                    <v-select
                      :items="['Checked']"
                      label="Checked"
                      outlined
                      dense
                      hide-details="auto"
                    ></v-select>
                    <span class="mx-5">By</span>
                    <v-select
                      :items="['Email']"
                      label="Email"
                      outlined
                      dense
                      hide-details="auto"
                    ></v-select>
                  </div> -->
                </div>
              </div>
            </v-card-text>
          </v-card>
          <div
            class="d-flex align-center"
            style="cursor: pointer"
            @click="addNewItem"
          >
            <v-icon>mdi-plus-circle-outline</v-icon>
            <span class="ml-2">Add Checklist Item</span>
          </div>
        </div>
      </v-form>
      <span
        v-if="singleSelectionMultipleDefaultsError"
        class="text-caption red--text"
        >Single selection checklist should have only one default selected
        item.</span
      >
      <v-sheet class="d-md-flex">
        <v-sheet class="flex-column"> </v-sheet>
        <v-spacer></v-spacer>

        <v-sheet class="justify-right text-right">
          <v-btn @click="goBack" plain class="mr-4"> Back </v-btn>

          <v-btn @click="handleSubmit" color="primary" rounded x-large>
            {{ id ? "Update" : "Save" }}
          </v-btn>
        </v-sheet>
      </v-sheet>
    </v-card-text>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ChecklistService from "@/services/checklistService.js";
import { rules } from "../../utils/validation";
import moment from "moment";
import TeacherSelector from "../common/TeacherSelector.vue";
import StudentSelector from "../common/StudentSelector.vue";
import ClassroomSelector from "../common/ClassroomSelector.vue";

export default {
  components: { TeacherSelector, StudentSelector, ClassroomSelector },
  props: ["id", "isModal"],
  data() {
    return {
      loaded: false,
      valid: false,
      programList: [],
      checklistTypes: [
        { text: "Daily", value: "DAILY" },
        { text: "Onetime", value: "ONETIME" },
      ],
      selectionTypes: [
        { text: "Single", value: "SINGLE" },
        { text: "Multiple", value: "MULTIPLE" },
      ],
      assignTypes: [
        { text: "Program", value: "PROGRAM" },
        { text: "Teacher", value: "TEACHER" },
        { text: "Classroom", value: "CLASSROOM" },
        { text: "Student", value: "STUDENT" },
      ],
      assignType: null,
      programIds: [],
      teacherIds: [],
      classroomIds: [],
      studentIds: [],
      checklist: {
        id: null,
        checklistName: null,
        checklistDescription: null,
        allowGlobalCheck: false,
        allowAttachment: false,
        mandatory: false,
        items: [],
        type: "DAILY",
        showOnDashboard: false,
        selectionMethod: "MULTIPLE",
      },
      limited: false,
      validFrom: null,
      validTo: null,
      rules: {
        ...rules,
        requiredMultipleAutocomplete: value => {
          if (value.length < 1) {
            return "This field is required";
          }
          return true;
        },
      },
    };
  },
  created() {
    this.initPrograms();
    this.setInstance();
  },
  computed: {
    ...mapGetters("navigation", ["globalCenter"]),
    ...mapGetters("auth", ["getCenterName"]),
    singleSelectionMultipleDefaultsError: function() {
      const defaultCheckedItems = this.checklist.items.filter(
        item => item.defaultStatus === "CHECKED",
      );

      return (
        this.checklist.selectionMethod === "SINGLE" &&
        defaultCheckedItems.length > 1
      );
    },
  },
  watch: {
    checklist: {
      handler(value) {
        if (value.type === "ONETIME") {
          this.limited = true;
        } else {
          this.limited = false;
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("program", ["fetchPrograms"]),

    async handleSubmit() {
      // validate form
      if (!this.$refs.form.validate()) return;

      if (this.singleSelectionMultipleDefaultsError) {
        return;
      }

      try {
        let response;
        if (this.checklist.id) {
          const defaultCheckedItems = this.checklist.items?.filter(
            item => item.defaultStatus === "CHECKED",
          );

          //selection type is single and no any default sections, make first item as default
          if (
            this.checklist.selectionMethod === "SINGLE" &&
            this.checklist.items?.length &&
            !defaultCheckedItems.length
          ) {
            this.checklist.items[0].defaultStatus = "CHECKED";
          }

          const data = this.checklist;
          if (this.limited) {
            data.validFrom = this.validFrom;
            data.validTo = this.validTo;
          }
          // assign empty assign types to the data
          data.programIds = null;
          data.teacherIds = null;
          data.classroomIds = null;
          data.studentIds = null;
          // check assign type
          switch (this.assignType) {
            case "PROGRAM":
              data.programIds = this.programIds;
              break;
            case "TEACHER":
              data.teacherIds = this.teacherIds;
              break;
            case "CLASSROOM":
              data.classroomIds = this.classroomIds;
              break;
            case "STUDENT":
              data.studentIds = this.studentIds;
              break;
          }
          // If there's an ID, it's an update operation
          response = await ChecklistService.updateChecklist(
            this.checklist.id,
            data,
          );
        } else {
          const defaultCheckedItems = this.checklist.items?.filter(
            item => item.defaultStatus === "CHECKED",
          );
          //selection type is single and no any default sections, make first item as default
          if (
            this.checklist.selectionMethod === "SINGLE" &&
            this.checklist.items?.length &&
            !defaultCheckedItems.length
          ) {
            this.checklist.items[0].defaultStatus = "CHECKED";
          }
          const data = this.checklist;
          if (this.limited) {
            data.validFrom = this.validFrom;
            data.validTo = this.validTo;
          }
          // assign empty assign types to the data
          data.programIds = null;
          data.teacherIds = null;
          data.classroomIds = null;
          data.studentIds = null;
          // check assign type
          switch (this.assignType) {
            case "PROGRAM":
              data.programIds = this.programIds;
              break;
            case "TEACHER":
              data.teacherIds = this.teacherIds;
              break;
            case "CLASSROOM":
              data.classroomIds = this.classroomIds;
              break;
            case "STUDENT":
              data.studentIds = this.studentIds;
              break;
          }
          // If there's no ID, it's a create operation
          response = await ChecklistService.createChecklist(data);
        }
        // Success handling here
        console.log("Success:", response.data); // Accessing response data
        this.addSuccessMessage("Checklist Saved");
        // Redirect or provide feedback as necessary
        this.$emit("clicked");
        this.$refs.form.reset();
        this.checklist.items = [];
      } catch (error) {
        // Error handling
        console.error("Error during checklist submission:", error);
        const errorMessage =
          error.response?.data?.message || "An unexpected error occurred";
        // Show error message to the user
        // Make sure you have a mechanism in your component to display error messages
        this.addErrorMessage(errorMessage);
      }
    },
    goBack() {
      if (this.isModal) {
        this.onCloseDialog();
      } else {
        this.$router.go(-1);
      }
    },
    setInstance() {
      if (this.id) {
        ChecklistService.getChecklistById(this.id)
          .then(resp => {
            this.checklist = resp.data;

            if (resp.data.validFrom) {
              this.limited = true;
              this.validFrom = resp.data.validFrom;
              this.validTo = resp.data.validTo;
            }

            // check student ids are available
            if (resp.data.studentIds) {
              this.assignType = "STUDENT";
              this.studentIds = resp.data.studentIds;
            }
            // check classroom ids are available
            if (resp.data.classroomIds) {
              this.assignType = "CLASSROOM";
              this.classroomIds = resp.data.classroomIds;
            }
            // check teacher ids are available
            if (resp.data.teacherIds) {
              this.assignType = "TEACHER";
              this.teacherIds = resp.data.teacherIds;
            }
            // check program ids are available
            if (resp.data.programIds) {
              this.assignType = "PROGRAM";
              this.programIds = resp.data.programIds;
            }

            // remove uncontrolled fields
            delete this.checklist.validFrom;
            delete this.checklist.validTo;
            delete this.checklist.programIds;
            delete this.checklist.teacherIds;
            delete this.checklist.classroomIds;
            delete this.checklist.studentIds;
          })
          .catch(error => {
            const errorMessage =
              error.data?.message || "An unexpected error occurred";
            this.addErrorMessage(errorMessage);
          });

        this.loaded = true;
      } else {
        this.loaded = true;
      }
    },
    onCloseDialog() {
      this.id = null;
      this.$emit("clicked", "closeChecklistDialog");
      this.$refs.form.reset();
      this.checklist.items = [];
    },
    addNewItem() {
      this.checklist.items.push({
        id: null,
        itemName: "",
        itemDescription: "",
      });
    },
    handleCheckbox(itemIndex) {
      const modifiedChecklistItems = this.checklist.items.map((item, index) => {
        const defaultStatus = index === itemIndex ? "CHECKED" : "UNCHECKED";
        return { ...item, defaultStatus };
      });
      this.checklist.items = modifiedChecklistItems;
    },
    deleteItem(index) {
      this.checklist.items.splice(index, 1);
    },
    initPrograms() {
      const params = {
        page: 0,
        size: 1000,
        sort: "name",
        centerId: this.globalCenter.id,
      };
      this.fetchPrograms(params)
        .then(response => {
          if (response) {
            this.programList = response.content;
          }
        })
        .catch(error => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    getProgramText(item) {
      return `${item?.name} (${this.getCenterName(item?.centerId)})`;
    },
    selectAllPrograms() {
      this.checklist.programIds = this.programList.map(program => program.id);
    },
    validDateRange() {
      if (!this.limited) return true;

      // Check if startDate is before endDate
      if (moment(this.validTo).isBefore(moment(this.validFrom))) {
        return "Valid to cannot be earlier than valid from.";
      }

      return true;
    },
    validValidFrom() {
      if (!this.limited) return true;

      // check null
      if (!this.validTo) {
        return "Please select valid from date";
      }

      // Check if startDate is before endDate
      if (moment(this.validTo).isBefore(moment(this.validFrom))) {
        return "Valid to cannot be earlier than valid from.";
      }

      return true;
    },
    validValidTo() {
      if (!this.limited) return true;

      // check null
      if (!this.validTo) {
        return "Please select valid to date";
      }

      return true;
    },
  },
};
</script>
